import React, { useState, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import useDismiss, { DISMISS_REASON } from '@hooks/useDismiss';
import { TOOLTIP_KEY } from './constants';
import { getNextTabbableAfter, getTabbables, isInside } from './helpers';
import './index.less';

// Opens on hover and on keyboard focus within its target, and closes on Escape. When the
// tooltip holds links (or any tabbable), Tab carries focus from the target into the portalled
// box and on to whatever follows the target, so its content is reachable without a mouse.
const Tooltip = ({ title, children }) => {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [measured, setMeasured] = useState(false);
    const targetRef = useRef(null);
    const tooltipRef = useRef(null);

    const showTooltip = () => {
        setVisible(true);
    };

    const hideTooltip = () => {
        setVisible(false);
        setMeasured(false);
    };

    const focusTarget = () => getTabbables(targetRef.current).at(-1)?.focus();

    useDismiss(targetRef, (event, reason) => {
        if (reason !== DISMISS_REASON.ESCAPE) return;
        const focusWasInTooltip = isInside([tooltipRef.current], document.activeElement);
        if (focusWasInTooltip) focusTarget();
        hideTooltip();
    }, { enabled: visible });

    const hideOnFocusLeave = (event) => {
        if (!isInside([targetRef.current, tooltipRef.current], event.relatedTarget)) hideTooltip();
    };

    const onTargetKeyDown = (event) => {
        if (event.key !== TOOLTIP_KEY.TAB || event.shiftKey || !visible) return;
        const inTooltip = getTabbables(tooltipRef.current);
        if (!inTooltip.length || getTabbables(targetRef.current).at(-1) !== event.target) return;
        event.preventDefault();
        inTooltip[0].focus();
    };

    const onTooltipKeyDown = (event) => {
        if (event.key !== TOOLTIP_KEY.TAB) return;
        const inTooltip = getTabbables(tooltipRef.current);
        if (event.shiftKey && event.target === inTooltip[0]) {
            event.preventDefault();
            focusTarget();
        } else if (!event.shiftKey && event.target === inTooltip.at(-1)) {
            event.preventDefault();
            const next = getNextTabbableAfter(targetRef.current, tooltipRef.current);
            hideTooltip();
            next?.focus();
        }
    };

    useLayoutEffect(() => {
        if (visible && targetRef.current && tooltipRef.current) {
            const targetRect = targetRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();

            let top = targetRect.top + window.scrollY - tooltipRect.height - 8;
            let left = targetRect.left + window.scrollX + targetRect.width / 2;

            const padding = 8;
            const screenWidth = window.innerWidth;

            if (left - tooltipRect.width / 2 < padding)
                left = tooltipRect.width / 2 + padding;
            else if (left + tooltipRect.width / 2 > screenWidth - padding)
                left = screenWidth - tooltipRect.width / 2 - padding * 3;

            if (top < window.scrollY + padding)
                top = targetRect.bottom + window.scrollY + 8


            setPosition({ top: top - 4, left });
            setMeasured(true);
        }
    }, [visible, title]);

    if (!title) return <>{children}</>;

    return (
        <>
            <div
                className="tooltipContainer"
                onMouseOver={showTooltip}
                onMouseLeave={hideTooltip}
                onFocus={showTooltip}
                onBlur={hideOnFocusLeave}
                onKeyDown={onTargetKeyDown}
                ref={targetRef}
            >
                {children}
            </div>
            {visible &&
                ReactDOM.createPortal(
                    <div
                        ref={tooltipRef}
                        className="tooltip-box"
                        style={{
                            position: 'absolute',
                            top: `${position.top}px`,
                            left: `${position.left}px`,
                            transform: 'translateX(-50%)',
                            visibility: measured ? 'visible' : 'hidden',
                        }}
                        onMouseEnter={showTooltip}
                        onMouseLeave={hideTooltip}
                        onBlur={hideOnFocusLeave}
                        onKeyDown={onTooltipKeyDown}
                    >
                        {title}
                    </div>,
                    document.body
                )}
        </>
    );
};

export default Tooltip;